/*
 *   File : orders.js
 *   Author : https://evoqins.com
 *   Description : orders component
 *   Integrations : sub-admin/order-list, orders/filter-data
 *   Version : 1.0.1
 */
import React, { useState, useEffect } from "react";
import { Tabs } from "../../../Components/Navigation";
import { Filter } from "../../../Components/Filters";
import { DataTableComponent } from "../../../Components/DataTable";
import { Chip, EmptyScreen, Loader } from "../../../Components/Other";
import { PrimaryButton } from "../../../Components/Buttons";
import { useNavigate } from "react-router-dom";
import { APIService } from "../../../Service";
import { toast } from "react-toastify";

const TAB_DATA = [
  { id: 0, label: "all-orders", name: "All Orders" },
  { id: 1, label: "mutual-funds", name: "Mutual Funds" },
  { id: 2, label: "goals", name: "Goals" },
];

export default function Orders({ adminId }) {
  const navigate = useNavigate();
  const [appliedFilters, setAppliedFilters] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [filterData, setFilterData] = useState([]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [paginationData, setPaginationData] = useState(null);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const COLUMNS = [
    {
      name: "Date",
      selector: (row) => row.order_date,
      sortable: true,
      width: "160px",
    },

    {
      name: "User Name",
      selector: (row) => row.user_name,
      wrap: true,
      width: "200px",
      cell: (row) => (
        <div className="d-flex column-gap-2 align-items-center">
          <img
            src={require("../../../Assets/Images/avatar.jpg")}
            alt={row.user_name || "User"}
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <span>{row.user_name || "N/A"}</span>
        </div>
      ),
    },

    {
      name: "Order ID",
      selector: (row) => row.order_id,
      sortable: true,
      width: "160px",
      cell: (row) => row.display_order_id || `ORD-${row.order_id}`,
    },

    {
      name: "Order Type",
      selector: (row) => row.order_type,
      width: "160px",
    },

    {
      name: "Type",
      selector: (row) => row.type,
      cell: (row) => {
        return (
          <Chip
            text={row.type}
            style="px-2 py-1 bg-lavender-blue bg-opacity-43"
            textClr="text-facebook-blue"
          />
        );
      },
    },

    {
      name: "Quantity",
      selector: (row) => row.quantity,
      cell: (row) => row.quantity || "N/A",
    },

    {
      name: "Amount",
      selector: (row) => row.amount,
      cell: (row) =>
        row.amount
          ? typeof row.amount === "number"
            ? `₹${row.amount.toLocaleString("en-IN")}`
            : row.amount
          : "₹0",
    },

    {
      name: "Order Status",
      selector: (row) => row.order_status,
      width: "160px",
      cell: (row) => (
        <p
          className={`${
            row.order_status === "Success"
              ? "text-medium-sea-green"
              : row.order_status === "Pending"
                ? "text-facebook-blue"
                : "text-copper-red"
          } mb-0`}
        >
          {row.order_status}
        </p>
      ),
    },

    {
      name: "Payment Status",
      selector: (row) => row.payment_status,
      cell: (row) => (
        <p
          className={`${
            row.payment_status === "Success"
              ? "text-medium-sea-green"
              : row.payment_status === "Pending"
                ? "text-facebook-blue"
                : "text-copper-red"
          } mb-0`}
        >
          {row.payment_status}
        </p>
      ),
    },

    {
      name: "Action",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
          name="View order"
          onPress={() =>
            navigate(
              `/orders/detail/${
                row.order_type?.toLowerCase() || "order"
              }/${row.order_id}`,
            )
          }
        />
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  useEffect(() => {
    if (adminId) {
      _fetchOrderList();
    }
  }, [page, perPage, activeTab, adminId, appliedFilters]);

  // Select All covers every page of the current list, so only a different list clears it
  useEffect(() => {
    setIsAllSelected(false);
  }, [perPage, activeTab, adminId, appliedFilters]);

  useEffect(() => {
    _getFilterData();
  }, []);

  // -------------------------------
  // FILTER HANDLER
  // -------------------------------
  const handleApply = (selectedValues) => {
    console.log("Filter applied:", selectedValues);
    setAppliedFilters(selectedValues);
    setPage(1); // Reset to first page when filters change
  };

  // -------------------------------
  // FETCH ORDER LIST
  // -------------------------------
  const _fetchOrderList = () => {
    setLoader(true);
    setIsEmpty(false);

    const payload = {
      sub_admin_id: parseInt(adminId),
      page_num: page,
      page_size: perPage,
      tab_type: activeTab,
      ...appliedFilters,
    };

    APIService(true, "sub-admin/order-list", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const data = res.data;

          setTableData(data.orders || []);
          setTotalRows(data.total_count || 0);
          setPaginationData({
            totalPage: data.total_page,
            currentPage: data.current_page,
            page: data.page,
            record: data.record,
          });

          // Set isEmpty if no orders found
          if (!data.orders || data.orders.length === 0) {
            setIsEmpty(true);
          } else {
            setIsEmpty(false);
          }
        } else {
          toast.error(res.message || "Failed to load order list");
          setTableData([]);
          setTotalRows(0);
          setIsEmpty(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching order list:", error);
        toast.error("Failed to load order list");
        setTableData([]);
        setTotalRows(0);
        setIsEmpty(true);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  // -------------------------------
  // FETCH FILTER DATA
  // -------------------------------
  //service to get filterData (normalize so Filter always receives an array; support multiple API shapes)
  const _getFilterData = () => {
    APIService(false, "orders/filter-data")
      .then((res) => {
        if (!res) return;
        const raw = res.data ?? res.filters ?? res.orders ?? res;
        const list = Array.isArray(raw)
          ? raw
          : (raw?.filters ??
              raw?.data ??
              raw?.items ??
              raw?.filter_options ??
              raw?.orders ??
              []) ||
            [];
        setFilterData(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error("Failed to load orders filter data:", err);
      });
  };

  return (
    <div className="e-glass-card rounded-16 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center pt-24 px-24 pb-3">
        <Tabs
          data={TAB_DATA}
          defaultActive={activeTab}
          onChange={(tabId) => {
            console.log("ACTIVE TAB:", tabId);
            setActiveTab(tabId);
            setPage(1); // reset pagination on new tab
          }}
        />
        <Filter filters={filterData} onApply={handleApply} />
      </div>

      {loader ? (
        <Loader />
      ) : isEmpty ? (
        <EmptyScreen message={"No orders under this sub admin"} />
      ) : (
        <>
          {/* PAGINATED TABLE */}
          <DataTableComponent
            info={true}
            columns={COLUMNS}
            data={tableData}
            pagination={true}
            paginationServer={true}
            selectableRows={true}
            allSelected={isAllSelected}
            onSelectedRowsChange={(state) => setIsAllSelected(state.allSelected)}
            currentPage={page}
            paginationTotalRows={totalRows}
            paginationPerPage={perPage}
            onChangePage={(p) => setPage(p)}
            onChangeRowsPerPage={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            className="e-table-basic"
          />
        </>
      )}
    </div>
  );
}
